<script setup>
import { onMounted, computed } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useUsersStore } from '@/stores'
import { storeToRefs } from 'pinia'

const props = defineProps({
  maxItems: {
    type: Number,
    default: 50,
  },
})

const emit = defineEmits(['viewMember'])

const usersStore = useUsersStore()
const { suggestions, suggestionsLoading, myUser } = storeToRefs(usersStore)

const visibleSuggestions = computed(() => suggestions.value.slice(0, props.maxItems))

const showPlatinumBadge = user =>
  user?.isPlatinum || user?.membershipType === 'Platinum' || user?.membershipType === 'Trial'

const isFollowedOrPending = userId => {
  if (!myUser.value?.following) return false

  return myUser.value.following.some(id => {
    const idStr = typeof id === 'object' ? id._id || id.id || id : id

    return idStr === userId || idStr?.toString() === userId?.toString()
  })
}

const handleFollow = async user => {
  await usersStore.follow(user._id)
}

const handleDismiss = user => {
  usersStore.dismissSuggestion(user._id)
}

const handleViewMember = user => {
  emit('viewMember', user)
}

onMounted(() => {
  usersStore.getSuggestions()
})
</script>

<template>
  <div
    v-if="suggestionsLoading || visibleSuggestions.length > 0"
    class="people-you-may-know mb-4"
  >
    <!-- Loading skeletons -->
    <div v-if="suggestionsLoading && visibleSuggestions.length === 0">
      <VListItem
        v-for="i in 3"
        :key="'skeleton-' + i"
        class="pl-1 pr-0"
      >
        <VSkeletonLoader type="list-item-avatar-two-line" />
      </VListItem>
    </div>

    <!-- Suggestions -->
    <div v-else>
      <VListItem
        v-for="user in visibleSuggestions"
        :key="user._id"
        class="pl-1 pr-0 suggestion-person-row"
        @click="handleViewMember(user)"
      >
        <template #prepend>
          <VAvatar
            v-if="user.profileImageId"
            size="48"
            class="list-avatar"
          >
            <VImg
              :src="mediaUrl(user.profileImageId, 'thumb')"
              :alt="user.firstName"
            />
          </VAvatar>
          <VAvatar
            v-else
            size="48"
            class="list-avatar avatar-initials"
          >
            <span class="avatar-fallback-text">{{ user?.firstName?.[0]?.toUpperCase() }}{{ user?.lastName?.[0]?.toUpperCase() }}</span>
          </VAvatar>
        </template>

        <template #title>
          <span class="text-sm name-line">
            {{ user.firstName }} {{ user.lastName }}
            <VIcon
              v-if="user.verified"
              size="14"
              class="verified-check"
            >
              material-symbols:verified
            </VIcon>
            <span
              v-if="showPlatinumBadge(user)"
              class="text-xs platinum-label"
            >
              (Platinum)
            </span>
          </span>
        </template>
        <template #subtitle>
          <span class="text-xs">
            {{ user.company ? user.company : '' }}
            <VChip
              v-if="user.suggestionReason"
              size="x-small"
              variant="tonal"
              color="primary"
              class="ml-1"
            >
              {{ user.suggestionReason }}
            </VChip>
            <div
              v-if="user.favoritedRestaurantsCount > 0"
              class="text-medium-emphasis"
            >
              {{ user.favoritedRestaurantsCount }} Favorites
            </div>
          </span>
        </template>

        <template #append>
          <div class="suggestion-actions">
            <VBtn
              v-if="!isFollowedOrPending(user._id)"
              variant="tonal"
              size="small"
              width="fit-content"
              aria-label="Add Friend"
              class="suggestion-add-friend-btn"
              style="padding: 4px 12px !important;"
              @click.stop="handleFollow(user)"
            >
              <VIcon size="18">
                mdi-account-plus
              </VIcon>
            </VBtn>
            <VBtn
              v-else
              variant="tonal"
              size="small"
              width="fit-content"
              density="compact"
              color="secondary"
              class="requested-btn"
              disabled
              style="padding-left: 5px !important; padding-right: 5px !important;"
            >
              Requested
            </VBtn>
            <VBtn
              icon
              variant="text"
              size="x-small"
              class="suggestion-dismiss-btn"
              @click.stop="handleDismiss(user)"
            >
              <VIcon size="16">
                mdi-close
              </VIcon>
            </VBtn>
          </div>
        </template>
      </VListItem>
    </div>
    <VDivider class="mt-2" />
  </div>
</template>

<style lang="scss" scoped>
.name-line .verified-check {
  margin-left: 0px;
  margin-top: -2px;
  color: rgb(0, 149, 246);
}

.name-line .platinum-label {
  margin-left: 0px;
}

.list-avatar {
  margin-inline-end: 2px;
  margin-left: 2px;
}

.avatar-initials {
  background: #e9f4f3;
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 2px;
}

.avatar-fallback-text {
  font-size: 1rem;
  font-weight: 500;
}

.platinum-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

:deep(.v-list-item-title) {
  line-height: 0.5rem;
}

:deep(.suggestion-person-row .v-list-item__prepend) {
  width: auto;
}

:deep(.suggestion-person-row .v-list-item__spacer) {
  width: 4px;
}

:deep(.suggestion-person-row .v-list-item__append) {
  align-self: flex-end;
  padding-bottom: 2px;
}

.suggestion-actions {
  display: flex;
  align-items: flex-end;
}

.suggestion-add-friend-btn {
  width: 32px;
  min-width: 32px !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.suggestion-dismiss-btn {
  align-self: flex-end;
  margin-left: 4px;
  margin-bottom: 1px;
}

.requested-btn.v-btn--disabled {
  opacity: 1 !important;
  color: rgb(var(--v-theme-secondary)) !important;
  background: rgba(var(--v-theme-secondary), 0.16) !important;
}

.requested-btn.v-btn--disabled :deep(.v-btn__content) {
  color: inherit;
  font-weight: 600;
}
</style>
